import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React from "react";

import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";

export interface TabbedReportChartCardTab {
  value: string;
  label: React.ReactNode;
  leadingContent?: React.ReactNode;
}

export interface TabbedReportChartCardLegendItem {
  label: React.ReactNode;
  lineStyle?: "solid" | "dashed";
  color?: string;
}

interface TabbedReportChartCardProps {
  title: React.ReactNode;
  onExport?: () => void;
  exportDisabled?: boolean;
  tabs: TabbedReportChartCardTab[];
  selectedTab: string;
  onTabChange: (value: string) => void;
  tabsAriaLabel: string;
  showTabs?: boolean;
  legendItems?: TabbedReportChartCardLegendItem[];
  loadingOverlay?: React.ReactNode;
  children: React.ReactNode;
}

export const TabbedReportChartCard: React.FC<TabbedReportChartCardProps> = ({
  title,
  onExport,
  exportDisabled,
  tabs,
  selectedTab,
  onTabChange,
  tabsAriaLabel,
  showTabs = true,
  legendItems,
  loadingOverlay,
  children,
}) => (
  <StoreInsightsPanel title={title} onExport={onExport} exportDisabled={exportDisabled}>
    <Box sx={{ position: "relative" }}>
      <Box sx={{ visibility: loadingOverlay ? "hidden" : "visible" }}>
        {showTabs ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
            <ToggleButtonGroup
              value={selectedTab}
              exclusive
              size="small"
              onChange={(_, value: string | null) => value && onTabChange(value)}
              aria-label={tabsAriaLabel}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                border: 0,
                "& .MuiToggleButtonGroup-grouped": {
                  m: "0 !important",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "999px !important",
                },
              }}
            >
              {tabs.map((tab) => (
                <React.Fragment key={tab.value}>
                  {tab.leadingContent}
                  <ToggleButton
                    value={tab.value}
                    sx={{
                      minHeight: 36,
                      px: 1.75,
                      py: 0.5,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      lineHeight: 1.25,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      "&.Mui-selected, &.Mui-selected:hover": {
                        bgcolor: "primary.main",
                        borderColor: "primary.main",
                        color: "primary.contrastText",
                      },
                    }}
                  >
                    {tab.label}
                  </ToggleButton>
                </React.Fragment>
              ))}
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {showTabs && legendItems?.length ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: -1,
              mb: 1,
              color: "text.secondary",
              fontSize: 12,
            }}
          >
            {legendItems.map((item, index) => (
              <Box
                component="span"
                key={index}
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 20,
                    borderTop: "2px",
                    borderTopStyle: item.lineStyle ?? "solid",
                    borderColor: item.color ?? "primary.main",
                  }}
                />
                {item.label}
              </Box>
            ))}
          </Box>
        ) : null}
        {children}
      </Box>
      {loadingOverlay}
    </Box>
  </StoreInsightsPanel>
);
